import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import { roadmapTracks, roadmapFAQs, type RoadmapTrack, type RoadmapStep } from "../../mocks/roadmap";
import { useGrowStore } from "../../hooks/useGrowStore";
import CertificateModal from "./components/CertificateModal";

const LEVEL_COLOR: Record<string, { bg: string; text: string }> = {
  입문: { bg: "#E8F5E9", text: "#2E7D32" },
  중급: { bg: "#FFF9E6", text: "#CC9900" },
  고급: { bg: "#FCE4EC", text: "#C62828" },
};

function TrackProgressBar({ track, completedIds }: { track: RoadmapTrack; completedIds: number[] }) {
  const done = track.steps.filter((s) => completedIds.includes(s.classId)).length;
  const pct = Math.round((done / track.steps.length) * 100);
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-[#191F28]/50">{done}/{track.steps.length} 완료</span>
        <span className="font-bold" style={{ color: track.color }}>{pct}%</span>
      </div>
      <div className="h-1.5 bg-[#F2F4F6] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: track.color }}
        />
      </div>
    </div>
  );
}

function StepCard({
  step,
  index,
  isCompleted,
  isLocked,
  trackColor,
  onStart,
}: {
  step: RoadmapStep;
  index: number;
  isCompleted: boolean;
  isLocked: boolean;
  trackColor: string;
  onStart: () => void;
}) {
  const level = LEVEL_COLOR[step.level];
  return (
    <div className={`relative flex gap-4 ${isLocked ? "opacity-50" : ""}`}>
      {/* Timeline line */}
      <div className="flex flex-col items-center shrink-0">
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-black text-sm transition-all"
          style={{ backgroundColor: isCompleted ? "#00D873" : isLocked ? "#C8CDD4" : trackColor }}
        >
          {isCompleted ? <i className="ri-check-line text-lg" /> : <span>{index + 1}</span>}
        </div>
        {index < 2 && (
          <div
            className="w-0.5 flex-1 mt-1 min-h-[24px]"
            style={{ backgroundColor: isCompleted ? "#00D873" : "#E4E6EA" }}
          />
        )}
      </div>

      {/* Content */}
      <div
        className={`flex-1 bg-white rounded-2xl p-5 mb-4 border-2 transition-all ${
          isCompleted ? "border-[#00D873]/30" : "border-transparent"
        }`}
      >
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${trackColor}15` }}
          >
            <i className={`${step.icon} text-lg`} style={{ color: step.iconColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span
                className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                style={{ backgroundColor: level.bg, color: level.text }}
              >
                {step.level}
              </span>
              <span className="text-xs text-[#8B95A1] flex items-center gap-1">
                <i className="ri-time-line" />{step.duration}
              </span>
              <span className="ml-auto text-xs font-bold" style={{ color: trackColor }}>
                +{step.points}P
              </span>
            </div>
            <h4 className="text-base font-black text-[#191F28]">{step.title}</h4>
          </div>
        </div>
        <p className="text-xs text-[#8B95A1] leading-relaxed mb-4">{step.desc}</p>
        <button
          onClick={!isLocked ? onStart : undefined}
          disabled={isLocked}
          className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
            isCompleted
              ? "bg-[#00D873]/10 text-[#00D873]"
              : isLocked
              ? "bg-[#F2F4F6] text-[#C8CDD4] cursor-not-allowed"
              : "text-white hover:opacity-90"
          }`}
          style={!isCompleted && !isLocked ? { backgroundColor: trackColor } : {}}
        >
          {isCompleted ? "✓ 완료" : isLocked ? "🔒 이전 단계 먼저 완료하세요" : "지금 시작하기 →"}
        </button>
      </div>
    </div>
  );
}

function TrackCard({ track }: { track: RoadmapTrack }) {
  const [expanded, setExpanded] = useState(false);
  const [showCert, setShowCert] = useState(false);
  const { isClassCompleted } = useGrowStore();
  const navigate = useNavigate();
  const completedIds = track.steps.map((s) => s.classId).filter(isClassCompleted);
  const totalPoints = track.steps.reduce((sum, s) => sum + s.points, 0);
  const isTrackDone = completedIds.length === track.steps.length;

  return (
    <>
      <div
        className={`bg-white rounded-3xl overflow-hidden border-2 transition-all ${
          isTrackDone ? "border-[#00D873]/40" : "border-transparent"
        }`}
      >
        {/* Track Header */}
        <div className="p-7">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
                style={{ backgroundColor: track.lightBg }}
              >
                {track.emoji}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                    style={{ backgroundColor: track.lightBg, color: track.color }}
                  >
                    {track.skillTag}
                  </span>
                  {isTrackDone && (
                    <span className="text-xs font-bold bg-[#00D873]/10 text-[#00D873] px-2.5 py-0.5 rounded-full">
                      🏆 트랙 완료!
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-black text-[#191F28]">{track.title}</h3>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-base font-black" style={{ color: track.color }}>+{totalPoints}P</div>
              <div className="text-xs text-[#8B95A1]">완주 보상</div>
            </div>
          </div>
          <p className="text-sm text-[#8B95A1] leading-relaxed mb-5">{track.subtitle}</p>

          <div className="flex items-center gap-4 text-xs text-[#8B95A1] mb-5">
            <span className="flex items-center gap-1"><i className="ri-book-open-line" />{track.steps.length}개 클래스</span>
            <span className="flex items-center gap-1"><i className="ri-time-line" />총 {track.totalTime}</span>
          </div>

          <TrackProgressBar track={track} completedIds={completedIds} />

          {/* Certificate download button for completed tracks */}
          {isTrackDone && (
            <button
              onClick={() => setShowCert(true)}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-extrabold text-white transition-all cursor-pointer hover:opacity-90 hover:-translate-y-0.5"
              style={{ backgroundColor: track.color }}
            >
              <i className="ri-award-line text-base" />
              수료증 다운로드
              <span className="text-xs opacity-75 ml-1">PNG</span>
            </button>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-xl bg-[#F2F4F6] hover:bg-[#E8E9EB] transition-colors cursor-pointer text-[#191F28]"
          >
            {expanded ? "커리큘럼 접기" : "커리큘럼 보기"}
            <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-base`} />
          </button>
        </div>

        {/* Steps */}
        {expanded && (
          <div className="px-7 pb-7 border-t border-[#F2F4F6] pt-6">
            {track.steps.map((step, i) => {
              const done = isClassCompleted(step.classId);
              const prevDone = i === 0 || isClassCompleted(track.steps[i - 1].classId);
              return (
                <StepCard
                  key={step.classId}
                  step={step}
                  index={i}
                  isCompleted={done}
                  isLocked={!done && !prevDone && i > 0}
                  trackColor={track.color}
                  onStart={() => navigate(`/learn/${step.classId}`)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Certificate Modal */}
      {showCert && (
        <CertificateModal
          trackTitle={track.title}
          trackEmoji={track.emoji}
          trackColor={track.color}
          completedCount={completedIds.length}
          totalCount={track.steps.length}
          onClose={() => setShowCert(false)}
        />
      )}
    </>
  );
}

function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <div className="bg-white rounded-3xl p-7">
      <h3 className="text-xl font-black text-[#191F28] mb-5">자주 묻는 질문</h3>
      <div className="space-y-3">
        {roadmapFAQs.map((faq, i) => (
          <div key={i} className="rounded-2xl overflow-hidden border border-[#F2F4F6]">
            <button
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-[#F9F9F9] transition-colors"
            >
              <span className="text-sm font-bold text-[#191F28]">{faq.q}</span>
              <i className={`${openIdx === i ? "ri-subtract-line" : "ri-add-line"} text-[#8B95A1] shrink-0 ml-4`} />
            </button>
            {openIdx === i && (
              <div className="px-5 pb-5">
                <p className="text-sm text-[#8B95A1] leading-relaxed">{faq.a}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RoadmapPage() {
  const { store } = useGrowStore();
  const totalCompleted = store.completedClasses.length;

  return (
    <div className="min-h-screen bg-[#F2F4F6] font-sans">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-16">

        {/* Hero */}
        <div className="bg-[#191F28] rounded-3xl p-8 lg:p-10 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-[#00D873]/10 rounded-full translate-x-16 -translate-y-16 pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-bold bg-[#00D873]/20 text-[#00D873] px-3 py-1.5 rounded-full">
                🗺️ 학습 로드맵
              </span>
              {totalCompleted > 0 && (
                <span className="text-xs font-bold bg-white/10 text-white/70 px-3 py-1.5 rounded-full">
                  {totalCompleted}개 완료 중
                </span>
              )}
            </div>
            <h1 className="text-3xl lg:text-4xl font-black leading-tight mb-3">
              어디서부터 시작할지<br />
              <span className="text-[#00D873]">로드맵이 알려드릴게요</span>
            </h1>
            <p className="text-white/60 text-sm leading-relaxed max-w-md">
              입문부터 심화까지 순서대로 배우면 실력이 쌓이는 게 느껴져요.
              3가지 트랙 중 하나를 골라 오늘 시작해보세요.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: "학습 트랙", value: "3개", icon: "ri-route-line", color: "#FF8C00" },
            { label: "총 클래스", value: "8개", icon: "ri-book-2-line", color: "#E91E63" },
            { label: "완주 보너스", value: "300P", icon: "ri-gift-line", color: "#00D873" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-4 text-center">
              <div className="w-8 h-8 mx-auto flex items-center justify-center mb-2" style={{ color: s.color }}>
                <i className={`${s.icon} text-xl`} />
              </div>
              <div className="text-lg font-black text-[#191F28]">{s.value}</div>
              <div className="text-xs text-[#8B95A1]">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Track Cards */}
        <div className="space-y-5 mb-8">
          {roadmapTracks.map((track) => (
            <TrackCard key={track.id} track={track} />
          ))}
        </div>

        {/* FAQ */}
        <FAQSection />
      </div>
    </div>
  );
}
